export enum AppType {
  DASHBOARD = 'dashboard',
  /** Legacy combined admin (SUPER_ADMIN + TENANT_ADMIN). Prefer SUPER_ADMIN / TENANT_ADMIN portals on-prem. */
  ADMIN = 'admin',
  SUPER_ADMIN = 'super-admin',
  TENANT_ADMIN = 'tenant-admin',
}

export type PortalFlavor = 'super-admin' | 'tenant-admin' | 'dashboard' | 'admin'

export interface AppDetectionResult {
  appType: AppType
  portal: PortalFlavor
  isDashboard: boolean
  isAdmin: boolean
  isSuperAdminPortal: boolean
  isTenantAdminPortal: boolean
  hostname: string
  port: number | null
  isDevelopmentHostname: boolean
}

/**
 * Build-time portal flavor. Set VITE_APP_PORTAL when packaging separate images.
 * Values: super-admin | tenant-admin | dashboard | admin
 */
export const getPortalFlavor = (): PortalFlavor | null => {
  const raw = (import.meta.env.VITE_APP_PORTAL || '').toString().trim().toLowerCase()
  if (
    raw === 'super-admin' ||
    raw === 'tenant-admin' ||
    raw === 'dashboard' ||
    raw === 'admin'
  ) {
    return raw
  }
  return null
}

const portalToAppType = (portal: PortalFlavor): AppType => {
  switch (portal) {
    case 'super-admin':
      return AppType.SUPER_ADMIN
    case 'tenant-admin':
      return AppType.TENANT_ADMIN
    case 'dashboard':
      return AppType.DASHBOARD
    default:
      return AppType.ADMIN
  }
}

/**
 * Centralized function to detect which application is currently running.
 * Prefer VITE_APP_PORTAL (baked into Docker images); fall back to port/hostname.
 */
export const detectCurrentApp = (): AppDetectionResult => {
  const hostname = window.location.hostname
  const port = window.location.port ? parseInt(window.location.port, 10) : null

  const isDevelopmentHostname =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('dev')

  const fromEnv = getPortalFlavor()
  let appType: AppType

  if (fromEnv) {
    appType = portalToAppType(fromEnv)
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Dev ports: dashboard 5173, legacy admin 5174, tenant-admin 5174, super-admin 5175
    if (port === 5175) {
      appType = AppType.SUPER_ADMIN
    } else if (port === 5174) {
      appType = AppType.ADMIN
    } else if (port === 5173) {
      appType = AppType.DASHBOARD
    } else {
      const pathname = window.location.pathname
      if (
        pathname.includes('/dashboard/management/tenant') ||
        pathname.includes('/dashboard/management/reseller') ||
        pathname.includes('/dashboard/management/plan')
      ) {
        appType = AppType.ADMIN
      } else {
        appType = AppType.DASHBOARD
      }
    }
  } else if (
    hostname.startsWith('super-admin.') ||
    hostname === 'super-admin.local' ||
    hostname.includes('super-admin')
  ) {
    appType = AppType.SUPER_ADMIN
  } else if (
    hostname.startsWith('tenant-admin.') ||
    hostname === 'tenant-admin.local' ||
    hostname.includes('tenant-admin')
  ) {
    appType = AppType.TENANT_ADMIN
  } else if (hostname === 'admin.dev.scopetech.ai' || hostname.startsWith('admin.')) {
    appType = AppType.ADMIN
  } else {
    appType = AppType.DASHBOARD
  }

  const portal: PortalFlavor =
    appType === AppType.SUPER_ADMIN
      ? 'super-admin'
      : appType === AppType.TENANT_ADMIN
        ? 'tenant-admin'
        : appType === AppType.DASHBOARD
          ? 'dashboard'
          : 'admin'

  const isAdminSurface =
    appType === AppType.ADMIN ||
    appType === AppType.SUPER_ADMIN ||
    appType === AppType.TENANT_ADMIN

  return {
    appType,
    portal,
    isDashboard: appType === AppType.DASHBOARD,
    isAdmin: isAdminSurface,
    isSuperAdminPortal: appType === AppType.SUPER_ADMIN,
    isTenantAdminPortal: appType === AppType.TENANT_ADMIN,
    hostname,
    port,
    isDevelopmentHostname,
  }
}

/**
 * Check if user role is allowed for current app / portal image.
 */
export const isUserRoleAllowedForApp = (userRole: string, appType: AppType): boolean => {
  if (appType === AppType.SUPER_ADMIN) {
    return userRole === 'SUPER_ADMIN' || userRole === 'RESELLER_ADMIN'
  }
  if (appType === AppType.TENANT_ADMIN) {
    return userRole === 'TENANT_ADMIN'
  }
  if (appType === AppType.ADMIN) {
    return (
      userRole === 'SUPER_ADMIN' || userRole === 'TENANT_ADMIN' || userRole === 'RESELLER_ADMIN'
    )
  }
  // Dashboard
  return userRole === 'TENANT_ADMIN' || userRole === 'TENANT_USER'
}

/**
 * Get default redirect route based on user role and app type
 */
export const getDefaultRoute = (userRole: string, appType: AppType): string => {
  if (appType === AppType.SUPER_ADMIN || (appType === AppType.ADMIN && userRole === 'SUPER_ADMIN')) {
    return '/dashboard/management/tenants'
  }
  if (
    appType === AppType.TENANT_ADMIN ||
    (appType === AppType.ADMIN && userRole === 'TENANT_ADMIN')
  ) {
    return '/dashboard/management/users'
  }
  if (appType === AppType.ADMIN) {
    return '/dashboard/management/tenants'
  }
  return '/dashboard'
}

/**
 * Detect if the current device is iOS
 */
export const isIOSDevice = (): boolean => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/**
 * Detect if the current device is Android
 */
export const isAndroidDevice = (): boolean => {
  return /Android/.test(navigator.userAgent)
}

/**
 * Get the appropriate App Store URL based on device
 */
export const getAppStoreUrl = (appId: string, webUrl: string): string => {
  if (isIOSDevice()) {
    const cleanAppId = appId.replace(/^id/, '')
    return `itms-apps://apps.apple.com/app/id${cleanAppId}`
  }
  return webUrl
}

/**
 * Get the appropriate Play Store URL based on device
 */
export const getPlayStoreUrl = (packageId: string, webUrl: string): string => {
  if (isAndroidDevice()) {
    return `market://details?id=${packageId}`
  }
  return webUrl
}
